<template>
  <Header ref="headerRef" @click="trackClicked" @new-chat="handleNewChat" />

  <ChatHistoryModal @new-chat="handleNewChat" @open-chat="goToChat" />

  <!-- Chat UI: the only scrolling element. min-h-0 lets this flex child shrink
       below its content so overflow-y-auto actually engages. overscroll-y-contain
       stops a swipe at the top/bottom from chaining to the WKWebView's root
       scroll view, which on iOS would otherwise rubber-band and drag the native
       modal down, exposing the background behind it. -->
  <main
    ref="scrollContainerRef"
    :[SCROLL_CONTAINER_ATTRIBUTE]="''"
    class="min-h-0 flex-1 overflow-y-auto overscroll-y-contain"
    @scroll="handleScroll"
  >
    <RevContainer>
      <div
        class="flex min-w-0 flex-col space-y-20 pt-60"
        :style="{
          paddingBottom: `${bottomPaddingPx}px`,
          transition: animatePadding ? 'padding-bottom 300ms ease-out' : 'none',
        }"
      >
        <div class="flex min-w-0 flex-col space-y-20">
          <!-- Boot placeholder: same per-event wrapper as a real message, so
               it occupies the exact slot the first message will render into,
               with no layout jump when it's replaced. -->
          <div
            v-if="isLoading && isWaitingForFirstChunk"
            class="flex min-w-0 flex-col py-10"
          >
            <LoadingCharacters />
          </div>

          <template v-for="(event, index) in events" :key="index">
            <div
              v-if="event.type !== 'categorized_prompt_suggestion'"
              :ref="setEventRef(index)"
              class="flex min-w-0 flex-col py-10 scroll-mt-80"
            >
              <!-- User message -->
              <UserMessage
                v-if="
                  event.type === 'message' && event.role === EVENT_ROLE_USER
                "
              >
                {{ event.content }}
              </UserMessage>

              <!-- Assistant message -->
              <AssistantMessage
                v-else-if="
                  event.type === 'message' &&
                  event.role === EVENT_ROLE_ASSISTANT
                "
              >
                <MarkdownText :text="event.content" />
              </AssistantMessage>

              <!-- Product recommendations -->
              <section
                v-else-if="event.type === 'product_recommendation'"
                class="w-full"
              >
                <ProductCardCarousel
                  :product-granularity
                  :products="toCarouselProducts(event.products)"
                  :tracking="{
                    list: 'ai_shopping_assistant',
                    source: 'ai_shopping_assistant_recommendation',
                  }"
                  @add-to-cart="handleAddToCart"
                  @link-click="trackProductClicked"
                />
              </section>

              <!-- Prompt suggestions -->
              <div v-else-if="event.type === 'prompt_suggestion'">
                <PromptSuggestions
                  :suggestions="event.prompts"
                  @click="handleFollowUpSuggestionClick"
                />
              </div>

              <!-- No products available -->
              <AssistantMessage
                v-else-if="event.type === 'no_product_recommendation'"
              >
                <MarkdownText :text="event.content" />
              </AssistantMessage>

              <p
                v-if="index === 0"
                class="mt-12 caption text-static-default-low"
              >
                {{ i18n(translations.disclaimer) }}
              </p>
            </div>
          </template>
        </div>

        <div v-if="streamedMessage" class="flex flex-col py-10">
          <AssistantMessage>
            <MarkdownText :text="revealedMessage" />
          </AssistantMessage>
        </div>

        <div
          v-if="!isLoading && isWaitingForFirstChunk"
          class="flex min-w-0 flex-col py-10 text-static-default-low"
        >
          <Loader />
        </div>
      </div>
    </RevContainer>
  </main>

  <!--
    Absolute (not fixed) so it anchors to the viewport-height root above,
    which already tracks the keyboard. bottom-0 therefore sits just above the
    keyboard when open, and above the home bar (safe-area inset) when closed.
  -->
  <aside
    ref="inputBarRef"
    class="absolute inset-x-0 bottom-0 pb-[calc(1.5rem+env(safe-area-inset-bottom))] safari-mobile-26:pb-[env(safe-area-inset-bottom)] z-10"
    :style="{
      background:
        'linear-gradient(to top, rgba(249, 247, 245, 0.8) 50%, rgba(249, 247, 245, 0) 100%)',
    }"
  >
    <!--
      Not RevContainer: the input keeps less horizontal padding (px-8) than
      the conversation (RevContainer px-24/lg:px-32), so it sits slightly
      wider than the messages and closer to the screen edges on narrow
      viewports — matching production. The max-width cap still prevents it
      from spanning full width on large viewports.
    -->
    <form
      class="relative mx-auto flex w-full max-w-[1184px] items-end justify-center px-8"
      @submit.prevent="handleSubmit('typed')"
    >
      <InputWithSuggestions
        :categories="categorizedSuggestionEvent?.categories ?? []"
        class="relative z-10 min-w-0 flex-1"
        :class="[
          showNewConversationButton ? 'mr-64' : 'mr-0',
          isNewConversationButtonEnabled &&
            'transition-[margin-right] duration-300 ease-in-out',
        ]"
        :show-suggestions
        @suggestion-clicked="handleSuggestionClick"
      >
        <ChatInput
          v-model="inputText"
          :button-label="i18n(translations.sendMessage)"
          :disabled="isLoading"
          :label="i18n(translations.inputPlaceholder)"
          :placeholder="isLoading ? '' : i18n(translations.inputPlaceholder)"
          @blur="inputFocused = false"
          @focus="inputFocused = true"
          @submit="handleSubmit('typed')"
        />
      </InputWithSuggestions>
      <RevButtonIcon
        v-if="isNewConversationButtonEnabled"
        :aria-label="i18n(translations.newConversation)"
        class="absolute bottom-0 right-8 z-0 !h-56 !w-56 bg-action-default-hi transition-opacity duration-300 ease-in-out"
        :class="showNewConversationButton ? 'opacity-100' : 'opacity-0'"
        :disabled="!showNewConversationButton"
        :icon="IconNewConversationFilled"
        size="small"
        type="button"
        variant="primary"
        @click="handleNewConversationClick"
      />
    </form>
  </aside>
</template>

<script lang="ts" setup>
import { useRuntimeConfig } from '#imports'
import {
  type ComponentPublicInstance,
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  useTemplateRef,
  watch,
} from 'vue'

import ProductCardCarousel from '@backmarket/nuxt-layer-recommendation/ProductCardCarousel.vue'
import { algoliaHitToVariant } from '@backmarket/nuxt-layer-recommendation/helpers.ts'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevContainer } from '@ds/components/Container'
import { IconNewConversationFilled } from '@ds/icons/IconNewConversationFilled'
import { useElementBounding } from '@vueuse/core'

import {
  type CategorizedPromptSuggestionEvent,
  type ChatEvent,
  type ChatRequestBody,
  type ChatRequestPageContext,
  EVENT_ROLE_ASSISTANT,
  EVENT_ROLE_USER,
  MESSAGE_ROLE_USER,
  type MessageInputMethod,
  type ProductRecommendationEvent,
  type StreamChunk,
  type StreamingEvent,
} from '~/api/shopping-assistant/types'

import { useFetchShoppingAssistantChatHistory } from '../composables/useFetchShoppingAssistantChatHistory'
import { useNewConversationDialog } from '../composables/useNewConversationDialog'
import { useSeededLaunch } from '../composables/useSeededLaunch'
import { useShoppingAssistantApi } from '../composables/useShoppingAssistantApi'
import type { EntryPoint } from '../composables/useShoppingAssistantContext'
import { useShoppingAssistantHost } from '../composables/useShoppingAssistantHost'
import {
  type ShoppingAssistantApiOutcome,
  useShoppingAssistantObservability,
} from '../composables/useShoppingAssistantObservability'
import { useShoppingAssistantState } from '../composables/useShoppingAssistantState'
import { useShoppingAssistantTracking } from '../composables/useShoppingAssistantTracking'
import { useTypewriter } from '../composables/useTypewriter'
import { SCROLL_CONTAINER_ATTRIBUTE } from '../constants'

import translations from './ShoppingAssistant.translations'
import AssistantMessage from './components/AssistantMessage.vue'
import ChatHistoryModal from './components/ChatHistoryModal.vue'
import ChatInput from './components/ChatInput.vue'
import Header from './components/Header/Header.vue'
import InputWithSuggestions from './components/InputWithSuggestions.vue'
import Loader from './components/Loader/Loader.vue'
import LoadingCharacters from './components/LoadingCharacters/LoadingCharacters.vue'
import MarkdownText from './components/MarkdownText.vue'
import PromptSuggestions from './components/PromptSuggestions.vue'
import UserMessage from './components/UserMessage.vue'

// Chat context, supplied by the host. The webview host derives it from the
// route; the web host will derive it from the launch store. The core stays
// route-agnostic and never reads the URL itself.
const props = withDefaults(
  defineProps<{
    initialChatId: string | null
    pageContext: ChatRequestPageContext | null
    entryPoint: EntryPoint
    seededMessage: string
    isSeededLaunch: boolean
    // Whether an unseeded mount may resume the user's latest server-side chat.
    // Webview resumes (default); the web opens a fresh chat each time (v1:
    // persist nothing).
    canResumeLatest?: boolean
  }>(),
  { canResumeLatest: true },
)

const config = useRuntimeConfig()

const { FF_SHOPPING_ASSISTANT_PRODUCT_GRANULARITY_PARENT } = config.public

const logger = useLogger()
const i18n = useI18n()
const { openErrorToast } = useTheToast()

const isStreamingEnabled = config.public.FF_SHOPPING_ASSISTANT_STREAMING
const isNewConversationButtonEnabled = Boolean(
  config.public.FF_SHOPPING_ASSISTANT_NEW_CONVERSATION_BUTTON,
)

const {
  inputText,
  events,
  isLoading,
  isProcessing,
  chatId,
  streamedMessage,
  resetState,
} = useShoppingAssistantState(props.initialChatId)

const host = useShoppingAssistantHost()
const { receivedEvent, clearReceivedEvent } = host

const chatHistory = useFetchShoppingAssistantChatHistory()

// Whether the conversation holds at least one real user message. Greeting and
// starter suggestions don't count — they carry nothing to lose, so the host can
// skip the destructive-action confirmation on a freshly created/empty chat.
const hasUserMessages = computed(() =>
  events.value.some(
    (event) => event.type === 'message' && event.role === EVENT_ROLE_USER,
  ),
)

// Whether the input textarea currently has focus. The bottom-right "new
// conversation" button is hidden while the user is typing (per spec: "input is
// not focused"), and when the conversation is empty ("conversation is not
// empty").
const inputFocused = ref(false)

const showNewConversationButton = computed(
  () =>
    isNewConversationButtonEnabled &&
    hasUserMessages.value &&
    !inputFocused.value,
)

watch(
  hasUserMessages,
  (value) => {
    if ('reportConversation' in host) host.reportConversation(value)
  },
  { immediate: true },
)

const eventRefs = new Map<number, HTMLElement>()
const headerRef = useTemplateRef<{ chromeEl: HTMLElement | null }>('headerRef')
const inputBarRef = useTemplateRef<HTMLElement>('inputBarRef')
// The only scrolling element; chat content scrolls inside it rather than the
// document, so all scroll math below is relative to this container.
const scrollContainerRef = useTemplateRef<HTMLElement>('scrollContainerRef')
const { height: headerHeight } = useElementBounding(
  computed(() => headerRef.value?.chromeEl ?? null),
)
const { height: inputBarHeight } = useElementBounding(inputBarRef)

// Small visual gap above the input bar so chat content doesn't sit
// flush against it.
const BREATHING_ROOM_PX = 8
const inputClearance = computed(() => inputBarHeight.value + BREATHING_ROOM_PX)

// Bottom padding (in px) below the chat content. Large during a submit so
// scrollUserMessageToTop has room to lift the prompt under the header;
// trimmed when the user scrolls up after streaming so the user can't
// scroll past content into white space. See expandBottomPadding /
// trimBottomPadding / handleScroll.
const bottomPaddingPx = ref(0)
// Animate padding-bottom only when shrinking (after streaming). Expanding
// to make room for scrollUserMessageToTop must be instant — otherwise the
// smooth scroll fires against a still-growing maxScroll and lands short.
const animatePadding = ref(false)
// After streaming, the expanded padding is left in place until the user
// scrolls up — trimming earlier would cause a visible scroll jump. When
// awaitingScrollUpTrim is true, handleScroll watches for an upward scroll
// and trims the padding at that point.
const awaitingScrollUpTrim = ref(false)
// True while we expect the assistant's first chunk after a submit (or
// chat start) and no chunk has arrived yet. Drives the boot placeholder and
// the reply-await Loader, and (combined with isLoading) avoids either
// flickering back on during the brief reactivity gap between
// flushStreamedMessage clearing streamedMessage and the finally that
// sets isProcessing = false.
const isWaitingForFirstChunk = ref(false)

// FIXME Backdoor to switch product granularity to "parent" in case the Add to cart button needs to be hidden.
const productGranularity = computed(() =>
  FF_SHOPPING_ASSISTANT_PRODUCT_GRANULARITY_PARENT ? 'parent' : 'backbox',
)

const pageContext = computed(() => props.pageContext)
const entryPoint = computed(() => props.entryPoint)
const seededMessage = computed(() => props.seededMessage)
const isSeededLaunch = computed(() => props.isSeededLaunch)

const {
  trackChatReady,
  trackMessageSent,
  trackClicked,
  trackProductClicked,
  trackProductRecommendationReceived,
} = useShoppingAssistantTracking(chatId, entryPoint)

const tracking = useTracking()
const { requestNewChat } = useNewConversationDialog()

const PROMPT_BAR_TRACKING_ZONE = 'ai_shopping_assistant_prompt_bar'

const { recordApiCall } = useShoppingAssistantObservability(
  host.hostType,
  entryPoint,
)

const categorizedSuggestionEvent = computed(() =>
  events.value.find(
    (event): event is CategorizedPromptSuggestionEvent =>
      event.type === 'categorized_prompt_suggestion',
  ),
)

// Initial prompt suggestions only: shown before the first user turn and
// only while the input is empty. Pasting a chip or typing hides the whole
// marquee (submission is always left to the user).
const showSuggestions = computed(
  () =>
    !isSeededLaunch.value &&
    categorizedSuggestionEvent.value !== undefined &&
    !hasUserMessages.value &&
    inputText.value === '',
)

function toCarouselProducts(products: ProductRecommendationEvent['products']) {
  return products
    .filter((p) => p.listingID != null)
    .map((p) => ({
      ...algoliaHitToVariant(p),
      specialOfferType: p.special_offer_type,
    }))
}

function handleAddToCart({ status }: { status: string }) {
  if (status === 'success') {
    if ('addToCart' in host) host.addToCart()
  } else if (status === 'error') {
    openErrorToast({ content: i18n(translations.addToCartError) })
  }
}

// Close any open prompt suggestions: the inline starter/follow-up chips
// (prompt_suggestion) and the input's suggestion dropdown (driven by
// categorized_prompt_suggestion). Called whenever a new user turn begins so
// stale suggestions don't linger behind it.
function dismissSuggestions() {
  events.value = events.value.filter(
    (event) =>
      event.type !== 'prompt_suggestion' &&
      event.type !== 'categorized_prompt_suggestion',
  )
}

watch(receivedEvent, (newEvent) => {
  if (newEvent) {
    // An injected message — native JS bridge, or the web "add to the ongoing
    // conversation" search entry — is a real user turn; sendAndTrackMessage
    // dismisses any open suggestions, exactly as a typed/clicked submit does.
    sendAndTrackMessage({ content: newEvent, inputMethod: 'suggested' })
    clearReceivedEvent()
  }
})

function setEventRef(index: number) {
  return (el: Element | ComponentPublicInstance | null) => {
    if (el) {
      eventRefs.set(index, el as HTMLElement)
    } else {
      eventRefs.delete(index)
    }
  }
}

const {
  startChat: apiStartChat,
  sendMessage: apiSendMessage,
  abortSendMessage,
  isSendMessageAborted,
  streamMessage: apiStreamMessage,
  streamStartChat: apiStreamStartChat,
} = useShoppingAssistantApi()

const {
  displayed: revealedMessage,
  drain: drainTypewriter,
  reset: resetTypewriter,
} = useTypewriter(streamedMessage)

function flushStreamedMessage() {
  if (streamedMessage.value) {
    processEvents([
      {
        type: 'message',
        role: EVENT_ROLE_ASSISTANT,
        content: streamedMessage.value,
      },
    ])
    streamedMessage.value = ''
  }
}

function handleStreamChunk(event: StreamChunk, skipTracking = false) {
  isWaitingForFirstChunk.value = false
  if (event.type === 'text_delta') {
    streamedMessage.value += event.content
  } else {
    flushStreamedMessage()
    processEvents([event], skipTracking)
  }
}

function processEvents(newEvents: ChatEvent[], skipTracking = false) {
  events.value.push(...newEvents)

  if (!skipTracking) {
    const receivedProducts = newEvents
      .filter(
        (event): event is ProductRecommendationEvent =>
          event.type === 'product_recommendation',
      )
      .flatMap(({ products }) => toCarouselProducts(products))

    trackProductRecommendationReceived(receivedProducts)
  }
}

async function startChat({
  chatId: existingChatId,
  canResumeLatest = false,
  withPageContext = false,
}: {
  chatId: string | null
  canResumeLatest?: boolean
  withPageContext?: boolean
}) {
  isWaitingForFirstChunk.value = true
  const body: ChatRequestBody = {
    context: 'Front Office User',
    by: config.public.ENVIRONMENT !== 'prod' ? 'tester' : 'user',
  }
  if (existingChatId === null) {
    // Page context (when present and allowed) takes precedence: it starts a
    // fresh chat scoped to the surface. Otherwise, resume the latest chat when
    // allowed. A seeded launch passes withPageContext without canResumeLatest,
    // so it stays force-new and never resumes.
    if (withPageContext && pageContext.value) {
      body.page_context = pageContext.value
    } else if (canResumeLatest) {
      body.resume_latest = true
    }
  }

  const startedAt = performance.now()
  let ttftMs: number | undefined
  let outcome: ShoppingAssistantApiOutcome = 'completed'
  let httpStatus: number | undefined
  let xRequestId: string | null = null

  try {
    if (isStreamingEnabled) {
      isProcessing.value = true

      await new Promise<void>((resolve, reject) => {
        let eventReceived = false
        apiStreamStartChat(existingChatId, body, {
          onEvent: (event: StreamingEvent) => {
            eventReceived = true
            ttftMs ??= performance.now() - startedAt
            if (event.type === 'chat_created') {
              chatId.value = event.id
              if ('syncChatId' in host) host.syncChatId(event.id)
            } else {
              handleStreamChunk(event, !!existingChatId)
            }
          },
          onEnd: async (terminatedBySentinel: boolean) => {
            if (!terminatedBySentinel && !eventReceived) {
              reject(new Error('Unexpected non-SSE response'))

              return
            }
            await drainTypewriter()
            flushStreamedMessage()
            resolve()
          },
          onAbort: () => {
            outcome = 'aborted'
            resolve()
          },
          onError: reject,
          onRequestId: (id) => {
            xRequestId = id
          },
        })
      })
    } else {
      const { data } = await apiStartChat(existingChatId, body, (id) => {
        xRequestId = id
      })

      if (!existingChatId) {
        chatId.value = data.id
        if ('syncChatId' in host) host.syncChatId(data.id)
      }

      processEvents(data.events, !!existingChatId)
    }

    trackChatReady()
  } catch (error) {
    outcome = 'errored'
    const err = error as { status?: number }
    httpStatus = err.status
    if (err.status === 404) {
      openErrorToast({
        content: i18n(translations.chatNotFound),
        actionLabel: i18n(translations.newChatButton),
        actionCallback: () => {
          if ('goToNewChat' in host) host.goToNewChat()
        },
      })
    } else {
      logger.error('[SHOPPING_ASSISTANT] Failed to create chat', {
        error: error as Error,
        owners: ['bot-group-ai-shopping-assistant-front'],
        chatId: chatId.value,
        hostType: host.hostType,
      })
      openErrorToast({
        content: i18n(translations.loadChatError),
        actionLabel: i18n(translations.retryButton),
        actionCallback: () => {
          startChat({
            chatId: existingChatId,
            canResumeLatest,
            withPageContext,
          })
        },
      })
    }
  } finally {
    recordApiCall({
      call: isStreamingEnabled ? 'stream_start_chat' : 'start_chat',
      outcome,
      durationMs: performance.now() - startedAt,
      ttftMs,
      chatId: chatId.value,
      xRequestId,
      httpStatus,
    })
    isLoading.value = false
    isProcessing.value = false
    isWaitingForFirstChunk.value = false
    scrollToBottom().then(trimBottomPadding)
  }
}

async function sendMessage({
  content,
  inputMethod,
}: {
  content: string
  inputMethod: MessageInputMethod
}): Promise<void> {
  if (!chatId.value) return

  const currentChatId = chatId.value
  const userMessageIndex = events.value.length
  // The endpoint always expects MESSAGE_ROLE_USER, regardless of how the message was entered.
  // This may revert to a conditional role in the future.
  // The typed/suggested distinction is preserved for tracking only.
  const role = MESSAGE_ROLE_USER

  const startedAt = performance.now()
  let ttftMs: number | undefined
  let outcome: ShoppingAssistantApiOutcome = 'completed'
  let httpStatus: number | undefined
  let xRequestId: string | null = null

  try {
    isProcessing.value = true
    isWaitingForFirstChunk.value = true
    awaitingScrollUpTrim.value = false

    // Add user message as a local event
    events.value.push({
      type: 'message',
      role: EVENT_ROLE_USER,
      content,
    })

    expandBottomPadding()
    scrollUserMessageToTop()

    if (isStreamingEnabled) {
      await new Promise<void>((resolve, reject) => {
        let eventReceived = false
        apiStreamMessage(
          currentChatId,
          { content, role },
          {
            onEvent: (event: StreamChunk) => {
              eventReceived = true
              ttftMs ??= performance.now() - startedAt
              handleStreamChunk(event)
            },
            onEnd: async (terminatedBySentinel: boolean) => {
              if (!terminatedBySentinel && !eventReceived) {
                reject(new Error('Unexpected non-SSE response'))

                return
              }
              await drainTypewriter()
              flushStreamedMessage()
              resolve()
            },
            onAbort: () => {
              outcome = 'aborted'
              resolve()
            },
            onError: reject,
            onRequestId: (id) => {
              xRequestId = id
            },
          },
        ).catch((err: Error) => {
          if (err.name === 'AbortError') {
            outcome = 'aborted'
            resolve()
          } else reject(err)
        })
      })
    } else {
      const data = await apiSendMessage(
        chatId.value,
        { content, role },
        (id) => {
          xRequestId = id
        },
      )
      processEvents(data.events)
    }
  } catch (error) {
    if (!isSendMessageAborted.value) {
      outcome = 'errored'
      httpStatus = (error as { status?: number }).status
      logger.error(
        `[SHOPPING_ASSISTANT] Failed to send message with inputMethod: ${inputMethod}`,
        {
          error: error as Error,
          owners: ['bot-group-ai-shopping-assistant-front'],
          chatId: currentChatId,
          hostType: host.hostType,
        },
      )
      openErrorToast({
        content: i18n(translations.sendMessageErrorContent),
        actionLabel: i18n(translations.retryButton),
        actionCallback: () => {
          events.value.splice(userMessageIndex, 1)
          sendMessage({ content, inputMethod })
        },
      })
    } else {
      outcome = 'aborted'
    }
  } finally {
    recordApiCall({
      call: isStreamingEnabled ? 'stream_message' : 'send_message',
      outcome,
      durationMs: performance.now() - startedAt,
      ttftMs,
      chatId: currentChatId,
      xRequestId,
      httpStatus,
    })
    isProcessing.value = false
    isWaitingForFirstChunk.value = false
    trimPaddingAfterStream()
  }
}

function getLastRenderedEventEl(): HTMLElement | null {
  for (let i = events.value.length - 1; i >= 0; i -= 1) {
    const el = eventRefs.get(i)
    if (el) return el
  }

  return null
}

// Offset of an element's bottom within the scroll container's content
// (i.e. its position in "scrollTop space"). Mirrors what `rect.bottom +
// window.scrollY` used to give in document space.
function eventBottomInContainer(
  el: HTMLElement,
  container: HTMLElement,
): number {
  return (
    el.getBoundingClientRect().bottom -
    container.getBoundingClientRect().top +
    container.scrollTop
  )
}

// Give the container enough room below the latest message that
// scrollUserMessageToTop can lift it all the way under the header.
function expandBottomPadding() {
  animatePadding.value = false
  const viewportHeight =
    scrollContainerRef.value?.clientHeight ?? window.innerHeight
  bottomPaddingPx.value = viewportHeight - headerHeight.value
}

// Cancel any pending scroll-up trim and trim the padding to
// inputClearance immediately.
function trimNow() {
  awaitingScrollUpTrim.value = false
  trimBottomPadding()
}

// After streaming, trim the padding in one of three ways:
// 1. Last event is below the visible area → trim immediately (padding
//    is entirely off-screen).
// 2. Padding is entirely off-screen (content fills the viewport) → trim
//    immediately (same as 1, reached via a different path).
// 3. Some padding is visible → partially trim the off-screen portion, then
//    wait for the user to scroll up to trim the rest (see handleScroll).
function trimPaddingAfterStream() {
  const container = scrollContainerRef.value
  const lastEl = getLastRenderedEventEl()
  if (!container || !lastEl) {
    trimNow()

    return
  }

  const vBottom = viewportBottom(container)
  const contentHeight = container.scrollHeight - bottomPaddingPx.value
  const visiblePadding = Math.max(inputClearance.value, vBottom - contentHeight)
  const lastEventBelowFold = eventBottomInContainer(lastEl, container) > vBottom
  const paddingEntirelyOffScreen = visiblePadding <= inputClearance.value

  if (lastEventBelowFold || paddingEntirelyOffScreen) {
    trimNow()
  } else {
    if (visiblePadding < bottomPaddingPx.value) {
      animatePadding.value = true
      bottomPaddingPx.value = visiblePadding
    }
    awaitingScrollUpTrim.value = true
  }
}

// The scroll position of the viewport's bottom edge in scrollTop space.
function viewportBottom(container: HTMLElement): number {
  return container.scrollTop + container.clientHeight
}

// Shrink the padding to just the input clearance. The browser clamps
// scrollTop to maxScroll = scrollHeight - clientHeight, so with
// paddingBottom = inputClearance the user can reach the end of the last
// event with breathing room above the input bar but cannot scroll past
// it into empty white space.
async function trimBottomPadding() {
  await nextTick()
  animatePadding.value = true
  bottomPaddingPx.value = inputClearance.value
}

// Trim the expanded padding only when the user has scrolled up far enough
// that the padding being removed is entirely below the fold. The exact
// threshold is dynamic: it's the maxScroll that will exist *after* the
// trim. Trimming below that point would cause the browser to clamp
// scrollTop, making the conversation visibly jump.
function handleScroll() {
  if (!awaitingScrollUpTrim.value) return
  const container = scrollContainerRef.value
  if (!container) return
  const maxScrollAfterTrim =
    container.scrollHeight -
    bottomPaddingPx.value +
    inputClearance.value -
    container.clientHeight
  if (container.scrollTop <= maxScrollAfterTrim) {
    trimNow()
  }
}

function scrollToBottom(): Promise<void> {
  // Re-assert scrollTop for ~500ms to absorb late layout shifts from streamed
  // content. Targets the last event's bottom (above the input bar) rather
  // than the content end, since the bottom padding may be viewport-sized.
  return new Promise<void>((resolve) => {
    const scroll = () => {
      const container = scrollContainerRef.value
      const el = getLastRenderedEventEl()
      if (!container || !el) return
      const targetTop =
        eventBottomInContainer(el, container) -
        container.clientHeight +
        inputClearance.value
      container.scrollTo?.({ top: targetTop, behavior: 'instant' })
    }
    nextTick(() => {
      scroll()
      const interval = setInterval(scroll, 50)
      setTimeout(() => {
        clearInterval(interval)
        resolve()
      }, 500)
    })
  })
}

async function scrollUserMessageToTop() {
  await nextTick()
  const container = scrollContainerRef.value
  const el = eventRefs.get(events.value.length - 1)
  if (!container || !el) return
  // Lift the message so its top sits just below the fading header chrome.
  const delta =
    el.getBoundingClientRect().top -
    container.getBoundingClientRect().top -
    headerHeight.value
  container.scrollTo?.({ top: container.scrollTop + delta, behavior: 'smooth' })
}

async function sendAndTrackMessage({
  content,
  inputMethod,
}: {
  content: string
  inputMethod: MessageInputMethod
}) {
  // A new user turn begins: dismiss any open suggestions first (see
  // dismissSuggestions) so stale chips don't linger behind the user's
  // message.
  dismissSuggestions()

  await sendMessage({ content, inputMethod })

  trackMessageSent({ inputMethod })
}

// Auto-send the initial message of a seeded launch. A seeded launch opens on a
// specific question, so the generic chat-creation intro (greeting + starter
// suggestions, already streamed into `events` by now) no longer makes sense —
// drop all of it so the conversation starts on the user's message. The AI
// disclaimer is rendered separately (under the first event) and is intentionally
// kept. The suggestions container was already hidden at mount (see onMounted).
// Tags the message as 'seeded'.
async function sendSeededMessage(content: string) {
  events.value = []

  await sendAndTrackMessage({ content, inputMethod: 'seeded' })
}

const { maybeLaunch: maybeSeededLaunch } = useSeededLaunch({
  isSeededLaunch,
  seededMessage,
  startChat,
  sendSeededMessage,
})

// Event Handlers

async function handleSubmit(inputMethod: MessageInputMethod) {
  const content = inputText.value.trim()
  if (!content || !chatId.value || isProcessing.value) return

  inputText.value = ''
  await sendAndTrackMessage({ content, inputMethod })
}

// Marquee chips paste only — submission is
// left to the user, and the marquee re-shows if the input is then cleared.
function handleSuggestionClick(text: string) {
  inputText.value = text
}

// Inline follow-up chips send immediately as a full user turn — which also
// dismisses the open chips via sendAndTrackMessage.
async function handleFollowUpSuggestionClick(text: string) {
  inputText.value = text
  await handleSubmit('suggested')
}

// Called when the user confirms "new chat" in the header. The component is
// not remounted (history.replaceState avoids Vue Router navigation), so we
// reset state and start a fresh session in-place.
async function handleNewChat() {
  abortSendMessage()
  resetState()
  resetTypewriter()
  eventRefs.clear()
  awaitingScrollUpTrim.value = false

  // A user-initiated new chat is intentionally blank: no page context, no resume.
  await startChat({
    chatId: null,
    canResumeLatest: false,
    withPageContext: false,
  })

  chatHistory.refresh()
}

function handleNewConversationClick() {
  requestNewChat(async () => {
    await handleNewChat()
    tracking.trackClick({ name: 'new_chat', zone: PROMPT_BAR_TRACKING_ZONE })
  })
}

async function goToChat(id: string) {
  abortSendMessage()
  resetState()
  resetTypewriter()
  eventRefs.clear()

  // resetState() nulls chatId, but startChat only restores it when creating a
  // chat (guarded on !existingChatId / the chat_created event), never when
  // resuming. Pre-seed it here — mirroring how the initial mount seeds chatId
  // from props.initialChatId — so sendMessage/handleSubmit (both guarded on
  // chatId.value) and host.syncChatId work once the resume completes.
  chatId.value = id

  // Reflect the resumed chat id in the URL immediately, since startChat with
  // an existing chatId never emits a chat_created event (the path that calls
  // host.syncChatId).
  if ('syncChatId' in host) host.syncChatId(id)

  await startChat({
    chatId: id,
    canResumeLatest: false,
    withPageContext: false,
  })
}

// Seed bottomPaddingPx from the input bar's first measurement, so chat content
// doesn't render flush against the fixed input bar before any submit happens.
const stopInitialPaddingWatcher = watch(inputBarHeight, (h) => {
  if (h > 0 && bottomPaddingPx.value === 0) {
    bottomPaddingPx.value = inputClearance.value
    stopInitialPaddingWatcher()
  }
})

// Initialization
onMounted(async () => {
  try {
    // A seeded launch (deep link) takes over: force-new chat + optional initial
    // message. Otherwise fall back to the normal resume/create-on-mount path.
    if (isSeededLaunch.value) {
      // Suppress the categorized starter-suggestions container up front. The
      // create response still carries starter suggestions (discarded by
      // sendSeededMessage), and without this the blue suggestions container would
      // briefly flash in during the create stream before being cleared.
      // showSuggestions computed checks isSeededLaunch, so no flag needed.
    }
    const handled = await maybeSeededLaunch()
    if (!handled) {
      await startChat({
        chatId: chatId.value,
        canResumeLatest: props.canResumeLatest,
        withPageContext: true,
      })
    }
  } finally {
    if ('ready' in host) host.ready()
  }
})

onUnmounted(() => {
  abortSendMessage()
})
</script>

<style scoped>
/* RevContainer's inner wrapper is a flex item that defaults to min-width:auto,
   so it refuses to shrink below its content and lets a wide markdown table widen
   the whole conversation (which then scrolls horizontally, since <main> is
   overflow-y-auto and thus overflow-x:auto too). Allow it to shrink to the panel
   width so the table's own overflow-x:auto scrolls it instead. */
:deep([data-test='container-wrapper']) {
  min-width: 0;
}
</style>
